import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { Appointment, AppointmentRequest, AppointmentUpdate } from '../models/appointment.model';

@Injectable({ providedIn: 'root' })
export class AppointmentApiService {
  private readonly baseUrl = '/api/appointments';
  private readonly adminTokenKey = 'atr-admin-token';
  private readonly web3FormsAccessKey = 'd03348d0-e362-4b44-97d2-52e3baea29b4';

  constructor(private readonly http: HttpClient) {}

  list(): Observable<Appointment[]> {
    return this.http.get<Appointment[]>(this.baseUrl);
  }

  listAdmin(): Observable<Appointment[]> {
    return this.http.get<Appointment[]>('/api/admin/appointments', { headers: this.adminHeaders() });
  }

  loginAdmin(pin: string): Observable<{ token: string }> {
    return this.http.post<{ token: string }>('/api/admin/login', { pin });
  }

  setAdminToken(token: string): void {
    sessionStorage.setItem(this.adminTokenKey, token);
  }

  clearAdminSession(): void {
    sessionStorage.removeItem(this.adminTokenKey);
    sessionStorage.removeItem('atr-admin');
  }

  create(request: AppointmentRequest): Observable<Appointment> {
    const body = new FormData();
    Object.entries(request).forEach(([key, value]) => {
      if (key === 'photos' || value === undefined || value === null) return;
      body.append(key, Array.isArray(value) ? JSON.stringify(value) : String(value));
    });
    request.photos?.forEach((photo) => body.append('photos', photo, photo.name));
    return this.http.post<Appointment>(this.baseUrl, body);
  }

  notifyWeb3Forms(request: AppointmentRequest): Observable<unknown> {
    return this.http.post('https://api.web3forms.com/submit', {
      access_key: this.web3FormsAccessKey,
      subject: 'New ATR Detailing quote request',
      from_name: 'ATR Detailing Website',
      name: request.name,
      email: request.email,
      phone: request.phone || '',
      vehicle: request.vehicle || '',
      service: request.service,
      add_ons: request.addOns?.join(', ') || '',
      preferred_date: request.preferredDate || '',
      preferred_time: request.preferredTime || '',
      mobile_detail: request.mobileDetail ? 'Yes' : 'No',
      mobile_address: request.mobileAddress || '',
      water_and_electricity_available: request.waterElectricityAvailable ? 'Yes' : 'No',
      message: request.message || '',
    });
  }

  update(id: string, update: AppointmentUpdate): Observable<Appointment> {
    return this.http.patch<Appointment>(`/api/admin/appointments/${id}`, update, { headers: this.adminHeaders() });
  }

  deletePhoto(appointmentId: string, filename: string): Observable<void> {
    return this.http.delete<void>(`/api/admin/appointments/${appointmentId}/photos/${encodeURIComponent(filename)}`, { headers: this.adminHeaders() });
  }

  deleteAppointment(appointmentId: string): Observable<void> {
    return this.http.delete<void>(`/api/admin/appointments/${appointmentId}`, { headers: this.adminHeaders() });
  }

  photoUrl(appointmentId: string, filename: string): string {
    const token = sessionStorage.getItem(this.adminTokenKey) || '';
    return `/api/admin/appointments/${appointmentId}/photos/${encodeURIComponent(filename)}?token=${encodeURIComponent(token)}`;
  }

  private adminHeaders(): { Authorization: string } {
    return { Authorization: `Bearer ${sessionStorage.getItem(this.adminTokenKey) || ''}` };
  }
}
